import { useEffect, useRef } from 'react';
import classNames from '../../../utils/classNames';

type ModalDialogProps = Omit<
    React.ComponentPropsWithoutRef<'dialog'>,
    'open' | 'onCancel' | 'onKeyDown' | 'onClick'
> & {
    open: boolean;
    onClose: () => void;
    canClose?: boolean;
    closeOnClickOutside?: boolean;
    closeOnEsc?: boolean;
    shake?: boolean;
};

export function ModalDialog({
    open,
    onClose,
    canClose = true,
    closeOnClickOutside = false,
    closeOnEsc = false,
    shake = false,
    className,
    children,
    ...props
}: ModalDialogProps) {
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return undefined;

        if (open) {
            if (!dialog.open) dialog.showModal();
        } else if (dialog.open) dialog.close();

        return undefined;
    }, [open]);

    // Block the native close event to maintain state control.
    // Escape key logic is centralized in `handleKeyDown`.
    function handleCancel(e: React.SyntheticEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    // Capture Escape key to enforce guard props (closeOnEsc, canClose) before closing.
    function handleKeyDown(e: React.KeyboardEvent<HTMLDialogElement>) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            if (closeOnEsc && canClose) {
                onClose();
            }
        }
    }

    // Handles clicks on the dialog to detect backdrop interactions.
    function handleClickOutside(e: React.MouseEvent<HTMLDialogElement>) {
        if (!canClose || !closeOnClickOutside) return;

        // If target equals currentTarget, the user clicked the backdrop, not the children.
        if (e.target === e.currentTarget) {
            onClose();
        }
    }

    return (
        <dialog
            ref={dialogRef}
            className={classNames(
                'w-full max-w-md rounded-lg bg-white p-0 shadow-lg outline-none',
                shake && 'animate-shake',
                className
            )}
            aria-modal="true"
            onCancel={handleCancel}
            onKeyDown={handleKeyDown}
            onClick={handleClickOutside}
            {...props}
        >
            {children}
        </dialog>
    );
}
